import { memo, ReactElement, useEffect, useRef, useState } from 'react'

import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { noop } from 'lodash'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { CreateCustomEventModal } from 'components/CreateResourceModal'
import { DEFAULT_SERIES, DEFAULT_TITLE } from 'configuration/charts'
import { date, type Dayjs } from 'configuration/dayjs'
import { DATES } from 'const/filters'
import { MONTH_DAY_FORMAT, MONTH_DAY_HOUR_FORMAT } from 'const/formatting'
import {
    REPORT_DATE_DAY_ID,
    REPORT_DATE_HOUR_ID,
    REPORT_DATE_WEEK_ID,
    SEARCH_TIME_HOUR_ID,
    SEARCH_TIME_WEEK_ID,
    SPLINE_AND_COLUMN,
} from 'const/widgets'
import { DEFAULT_TIME_SERIES_X_AXIS } from 'helpers/chart'
import {
    getDateRangeArray,
    isParallelPriorPeriod,
    presetRanges,
} from 'helpers/dateRange'
import { userHasDashboardsOnlyPermissions } from 'helpers/featurePermissions'
import { withCurrencyLocale } from 'helpers/formatting'
import { elementToString } from 'helpers/utilities'
import { transformToString } from 'helpers/widgets'
import {
    useCustomEventsForWidget,
    useModal,
    useUserHasPermissions,
} from 'hooks'
import { useDeepCompareMemo } from 'hooks/useDeepCompareMemo'
import useMappedChartColors from 'hooks/useMappedChartColors'
import { ReactComponent as CustomEventIcon } from 'images/annotation-icon-charts.svg'
import {
    selectAutomationCapabilitiesMap,
    selectBrandGroupsMap,
} from 'selectors/ui'
import {
    CurrencyCode,
    DataWidgetConfig,
    DateRangeFilter,
    FieldMapping,
    MetricType,
    PresetRange,
    PriorPeriodConfig,
    WidgetFilterMap,
} from 'types'

import { getChartType, legend, PointType, renderTooltip } from './helpers'
import { AxisDrivenSeries } from './helpers/axisDrivenSeries'
import { MetricsDrivenSeries } from './helpers/metricsDrivenSeries'

import 'highcharts/highcharts-more'
import 'highcharts/modules/annotations'

interface TimeSeriesWidgetProps {
    widget: DataWidgetConfig
    widgetData: any[]
    updateWidget?: (widget: DataWidgetConfig) => void
    widgetPriorPeriodData: any[]
    // Days between the current window's start and the comparison window's, as
    // reported by the server. Set for data sources that report the window they
    // compared against; the prior series is shifted forward by exactly this to
    // line up with the current one, instead of by a length derived from the
    // picked range.
    priorPeriodOffsetDays?: number
    // The window the server reported it queried, when it isn't the picked one.
    reportedRange?: string[]
    filters: WidgetFilterMap
    metricsConfig: FieldMapping
    chartHeight?: string | number
    showLegend?: boolean
    showYAxesTitles?: boolean
    currencyCode?: CurrencyCode
}

const TimeSeriesWidget = ({
    widget,
    widgetData,
    updateWidget = noop,
    widgetPriorPeriodData,
    priorPeriodOffsetDays,
    reportedRange,
    filters,
    metricsConfig,
    chartHeight,
    showLegend = true,
    showYAxesTitles = true,
    currencyCode,
}: TimeSeriesWidgetProps): ReactElement => {
    const chartRef = useRef<HighchartsReact.RefObject>(null)
    // We don't want to change the original widget
    const modifiedWidget = cloneDeep(widget)

    const { t } = useTranslation('dashboards')

    const { isModalVisible, toggleModalVisible, onModalCancel } = useModal()

    const [newEventDate, setNewEventDate] = useState<Dayjs | null>(null)

    const dashboardOnlyExperience = useUserHasPermissions(
        userHasDashboardsOnlyPermissions
    )

    const globalAddEventFnName = `openAddNewEventModal_${widget.id.replaceAll('-', '')}`

    // Remove global function on unmount
    // Used for tooltip markdown onclick event
    useEffect(() => {
        return () => {
            if ((window as any)[globalAddEventFnName]) {
                delete (window as any)[globalAddEventFnName]
            }
        }
    }, [globalAddEventFnName])

    const automationCapabilitiesMap = useSelector(
        selectAutomationCapabilitiesMap
    )

    const brandGroupsMap = useSelector(selectBrandGroupsMap)

    const widgetGroupBy = transformToString(widget.group_by)

    let isDataHourlyAggregated = [
        REPORT_DATE_HOUR_ID,
        SEARCH_TIME_HOUR_ID,
    ].includes(widgetGroupBy[0])
    const isDataWeeklyAggregated = [
        REPORT_DATE_WEEK_ID,
        SEARCH_TIME_WEEK_ID,
    ].includes(widgetGroupBy[0])
    const isDataMonthlyAggregated =
        widgetGroupBy[0]?.endsWith('_month') ?? false

    let customDateFormat = MONTH_DAY_FORMAT
    if (isDataHourlyAggregated) {
        customDateFormat = MONTH_DAY_HOUR_FORMAT
    }

    if (isDataHourlyAggregated && widget.isZoomedOut) {
        isDataHourlyAggregated = false
        modifiedWidget.sorter = {
            ...modifiedWidget.sorter,
            field: REPORT_DATE_DAY_ID,
        }
        modifiedWidget.group_by = modifiedWidget.group_by.map((grouped, idx) =>
            idx === 0 ? { ...grouped, id: REPORT_DATE_DAY_ID } : grouped
        )
        modifiedWidget.order_by = REPORT_DATE_DAY_ID
    }

    const modifiedWidgetGroupBy = transformToString(modifiedWidget.group_by)
    const customEvents = useCustomEventsForWidget(filters)

    const { mapElementToColor } = useMappedChartColors(widget.id)

    const priorPeriodConfig: PriorPeriodConfig = {
        diff: 1,
        unit: 'year',
        format: 'YYYY',
        hasData:
            !!modifiedWidget.include_change_metrics &&
            (widgetPriorPeriodData.length ?? 0) > 0,
    }

    // DOW-10016 backwards compatibility:
    //  If the widget has metrics_by_axis.total_metrics it's a new configuration that allows multiple y-axes at each side.
    //  Otherwise, we proceed with the older kind of configuration.
    const seriesHelper = modifiedWidget.metrics_by_axis?.total_metrics
        ? // Widgets created after 2022
          new AxisDrivenSeries(
              modifiedWidget,
              widgetData,
              widgetPriorPeriodData,
              priorPeriodConfig,
              automationCapabilitiesMap,
              brandGroupsMap,
              metricsConfig,
              mapElementToColor,
              showYAxesTitles
          )
        : // Widgets created before 2022
          // Maintenance only, new features should go to the other class
          new MetricsDrivenSeries(
              modifiedWidget,
              widgetData,
              widgetPriorPeriodData,
              priorPeriodConfig,
              automationCapabilitiesMap,
              brandGroupsMap,
              metricsConfig,
              mapElementToColor,
              showYAxesTitles
          )

    const dateRangeFilter = filters[DATES] as DateRangeFilter

    // The axis has to span the window the points came from. On a weekly source
    // that is wider than the picked range, and an axis pinned to the picked start
    // clips the leading bucket out of the plot while the chip and the totals still
    // count it — the chart would disagree with every other number on the widget.
    const dateRange = reportedRange
        ? reportedRange.map((day) => date(day, 'YYYY-MM-DD'))
        : getDateRangeArray(
              dateRangeFilter,
              isDataHourlyAggregated,
              filters.rangeLag !== undefined
                  ? Number(filters.rangeLag)
                  : undefined
          )

    if (priorPeriodOffsetDays !== undefined) {
        priorPeriodConfig.diff = priorPeriodOffsetDays
        priorPeriodConfig.unit = 'days'
        priorPeriodConfig.format = customDateFormat
    } else if (modifiedWidget.period_delta_type === 'prior_period') {
        const isParallel = isParallelPriorPeriod(dateRangeFilter)

        if (isParallel) {
            priorPeriodConfig.diff = 1
            priorPeriodConfig.unit =
                presetRanges()[dateRangeFilter as PresetRange].priorPeriodUnit!
        } else if (isDataMonthlyAggregated) {
            priorPeriodConfig.diff =
                dateRange[1].diff(dateRange[0], 'months') + 1
            priorPeriodConfig.unit = 'months'
        } else {
            priorPeriodConfig.diff = dateRange[1].diff(dateRange[0], 'days') + 1
            priorPeriodConfig.unit = 'days'
        }

        priorPeriodConfig.format = customDateFormat
    }

    const getMetricFormat = (point: PointType): string => {
        const { metric } = point.series.options as any
        return metricsConfig[metric]?.metricOptions?.format ?? ''
    }

    const getMetricType = (point: PointType): MetricType => {
        const { metric } = point.series.options as any
        return metricsConfig[metric]?.metricOptions?.type ?? 'count'
    }

    // Store function globally so tooltip can call it
    ;(window as any)[globalAddEventFnName] = (
        eventDate: string | number | undefined
    ) => {
        setNewEventDate(date(eventDate))
        toggleModalVisible()
    }

    // Show custom events for day-aggregated charts
    const showCustomEvents = (): boolean =>
        modifiedWidget.show_annotations !== false &&
        modifiedWidgetGroupBy[0]?.includes('day')

    // Currency axis/tooltip/reference-line formatting resolves the `$` symbol
    // from numeral's global locale (the dashboard currency). Market Analysis
    // series carry the segment marketplace's native currency, so scope every
    // format pass to the widget's resolved currencyCode. The wrapper is a no-op
    // when currencyCode is unset (every other data source / dashboard).
    const scopeFormatter = <
        F extends (this: never, ...args: never[]) => unknown,
    >(
        formatter: F
    ): F =>
        function scoped(this: unknown, ...args: unknown[]) {
            return withCurrencyLocale(currencyCode, () =>
                (
                    formatter as unknown as (
                        this: unknown,
                        ...a: unknown[]
                    ) => unknown
                ).apply(this, args)
            )
        } as unknown as F

    // getSeries() formats reference-line label values synchronously.
    const allSeries = withCurrencyLocale(currencyCode, () =>
        seriesHelper.getSeries()
    )
    const hiddenSeries = new Set(widget.hidden_series ?? [])
    allSeries.forEach((series) => {
        series.visible = !hiddenSeries.has(series.name)
    })

    const xAxis = {
        ...DEFAULT_TIME_SERIES_X_AXIS,
        min: dateRange[0].valueOf(),
        maxPadding: 0,
    }
    // Axis tick labels are formatted later inside Highcharts, so wrap each
    // formatter rather than relying on the active global locale at paint time.
    const yAxis = seriesHelper.getYAxes().map((axis) =>
        currencyCode && axis.labels?.formatter
            ? {
                  ...axis,
                  labels: {
                      ...axis.labels,
                      formatter: scopeFormatter(axis.labels.formatter),
                  },
              }
            : axis
    )

    const showPercentsInTooltips =
        modifiedWidget.stacked && modifiedWidget.stacked_type === 'percent'

    const baseTooltip = renderTooltip(
        t,
        getMetricFormat,
        showCustomEvents() ? customEvents : [],
        priorPeriodConfig,
        !dashboardOnlyExperience && showCustomEvents(),
        globalAddEventFnName,
        getMetricType,
        customDateFormat,
        isDataWeeklyAggregated,
        showPercentsInTooltips
    )
    // Tooltip values are formatted later inside Highcharts; scope its formatter
    // so hovered currency values render in the segment currency.
    const tooltip =
        currencyCode && baseTooltip.formatter
            ? {
                  ...baseTooltip,
                  formatter: scopeFormatter(baseTooltip.formatter),
              }
            : baseTooltip

    const chartOptions = {
        annotations: showCustomEvents()
            ? [
                  {
                      labels: customEvents.map((event) => ({
                          point: {
                              x: date(event.date).valueOf(),
                              y: 0,
                              xAxis: 0,
                          },
                          backgroundColor: 'transparent',
                          borderColor: 'transparent',
                          padding: 0,
                          useHTML: true,
                          formatter() {
                              return elementToString(<CustomEventIcon />)
                          },
                          y: -9,
                      })),
                      draggable: '',
                  },
              ]
            : [],
        chart: {
            marginTop: 50,
            height: chartHeight,
            spacing: [0, 6, 10, 0],
        },
        legend: showLegend
            ? {
                  ...seriesHelper.getLegendOptions(legend),
                  events: {
                      itemClick: (event: any) => {
                          const { legendItem } = event
                          const { name } = legendItem
                          if (name) {
                              // if we trigger a re-render while highcharts is
                              // handling an event, highcharts will throw an error
                              requestAnimationFrame(() => {
                                  updateWidget({
                                      ...modifiedWidget,
                                      hidden_series: hiddenSeries.has(name)
                                          ? modifiedWidget.hidden_series?.filter(
                                                (seriesName) =>
                                                    seriesName !== name
                                            )
                                          : [
                                                ...(modifiedWidget.hidden_series ??
                                                    []),
                                                name,
                                            ],
                                  })
                              })
                              event.preventDefault()
                          }
                          return true
                      },
                  },
              }
            : { enabled: false },
        plotOptions: {
            areaspline: {
                connectNulls: true,
                lineWidth: 1,
                marker: {
                    enabled: false,
                    lineColor: '#ffffff',
                },
            },
            area: {
                connectNulls: true,
                lineWidth: 1,
                marker: {
                    enabled: false,
                    lineColor: '#ffffff',
                },
            },
            column: {
                maxPointWidth: 60,
            },
            series: {
                ...DEFAULT_SERIES,
                stacking: modifiedWidget.stacked
                    ? (modifiedWidget.stacked_type ?? 'normal')
                    : undefined,
            },
            spline: {
                ...(getChartType(modifiedWidget) === SPLINE_AND_COLUMN
                    ? {
                          marker: {
                              lineColor: null,
                              lineWidth: null,
                          },
                      }
                    : {}),
            },
            line: {
                marker: {
                    enabled: false,
                },
            },
        },
        series: allSeries,
        title: DEFAULT_TITLE,
        tooltip,
        xAxis,
        yAxis,
    }

    // `immutable` makes Highcharts destroy and rebuild the chart whenever the
    // options object changes, so only hand it a new one when the chart's inputs
    // change. Other widgets writing to the shared chart colors map re-render
    // this one without changing its series colors.
    const seriesColors = allSeries.map((series) => series.color)
    // Preset ranges are resolved against today, so the axis can move without
    // any prop changing.
    const dateRangeValues = dateRange.map((day) => day.valueOf())
    const options = useDeepCompareMemo(
        () => chartOptions,
        [
            widget,
            widgetData,
            widgetPriorPeriodData,
            priorPeriodOffsetDays,
            reportedRange,
            filters,
            metricsConfig,
            chartHeight,
            showLegend,
            showYAxesTitles,
            currencyCode,
            automationCapabilitiesMap,
            brandGroupsMap,
            customEvents,
            dashboardOnlyExperience,
            updateWidget,
            t,
            seriesColors,
            dateRangeValues,
        ]
    )

    return (
        <>
            <div className="fg-chart-wrap line-chart-widget">
                <HighchartsReact
                    ref={chartRef}
                    immutable
                    highcharts={Highcharts}
                    options={options}
                />
            </div>
            <CreateCustomEventModal
                isModalVisible={isModalVisible}
                closeModal={onModalCancel}
                eventDate={newEventDate}
            />
        </>
    )
}

export default memo(TimeSeriesWidget, isEqual)
